import React, { useCallback, useEffect, useState } from "react";
import {
  Row,
  Col,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Card,
  CardHeader,
  CardBody,
} from "reactstrap";
import axios from "axios";
import swal from "sweetalert";

import Widget from "../../components/Widget/Widget";
import { API_TIMEOUT, PENYEDIA } from "../../utils";

const initialFetchState = {
  loading: false,
  error: null,
};

const TablePenyedia = () => {
  const [penyediaState, setPenyediaState] = useState(initialFetchState);
  const [penyediaList, setPenyediaList] = useState([]);

  const [penyediaModalOpen, setPenyediaModalOpen] = useState(false);

  const [namaPenyedia, setNamaPenyedia] = useState("");
  const [tahunPenyedia, setTahunPenyedia] = useState("");

  const [editingPenyedia, setEditingPenyedia] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleRequestError = useCallback((message, error) => {
    const responseMessage =
      error?.response?.data?.message || message || "Terjadi kesalahan";
    swal("Error", responseMessage, "error");
  }, []);

  const fetchPenyedia = useCallback(async () => {
    setPenyediaState({ loading: true, error: null });
    try {
      const response = await axios.get(PENYEDIA, {
        timeout: API_TIMEOUT,
      });
      setPenyediaList(response.data?.data || []);
      setPenyediaState({ loading: false, error: null });
    } catch (error) {
      setPenyediaState({ loading: false, error });
      handleRequestError("Gagal memuat data penyedia", error);
    }
  }, [handleRequestError]);

  useEffect(() => {
    fetchPenyedia();
  }, [fetchPenyedia]);

  const resetPenyediaForm = () => {
    setNamaPenyedia("");
    setTahunPenyedia("");
    setEditingPenyedia(null);
  };

  const togglePenyediaModal = () => {
    setPenyediaModalOpen((prev) => !prev);
    if (penyediaModalOpen) {
      resetPenyediaForm();
    }
  };

  const handleAddPenyedia = () => {
    resetPenyediaForm();
    setPenyediaModalOpen(true);
  };

  const handleEditPenyedia = (item) => {
    setEditingPenyedia(item);
    setNamaPenyedia(item.nama || "");
    setTahunPenyedia(item.tahun ? String(item.tahun) : "");
    setPenyediaModalOpen(true);
  };

  const handleDeletePenyedia = (item) => {
    swal({
      title: "Hapus penyedia?",
      text: `Penyedia "${item.nama}" akan dihapus permanen.`,
      icon: "warning",
      buttons: ["Batal", "Hapus"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;
      try {
        await axios.delete(`${PENYEDIA}${item.id}`, {
          timeout: API_TIMEOUT,
        });
        swal("Sukses", "Data penyedia berhasil dihapus", "success");
        fetchPenyedia();
      } catch (error) {
        handleRequestError("Gagal menghapus penyedia", error);
      }
    });
  };

  const handleSavePenyedia = async (event) => {
    event.preventDefault();
    if (!namaPenyedia.trim()) {
      swal("Validasi", "Nama penyedia wajib diisi", "warning");
      return;
    }
    if (!tahunPenyedia || isNaN(Number(tahunPenyedia))) {
      swal("Validasi", "Tahun harus berupa angka", "warning");
      return;
    }

    const payload = {
      nama: namaPenyedia.trim(),
      tahun: Number(tahunPenyedia),
    };

    setSaving(true);
    try {
      if (editingPenyedia) {
        await axios.patch(`${PENYEDIA}${editingPenyedia.id}`, payload, {
          timeout: API_TIMEOUT,
        });
        swal("Sukses", "Data penyedia berhasil diperbarui", "success");
      } else {
        await axios.post(PENYEDIA, payload, {
          timeout: API_TIMEOUT,
        });
        swal("Sukses", "Data penyedia berhasil ditambahkan", "success");
      }
      setSaving(false);
      setPenyediaModalOpen(false);
      resetPenyediaForm();
      fetchPenyedia();
    } catch (error) {
      setSaving(false);
      handleRequestError("Gagal menyimpan penyedia", error);
    }
  };

  const renderLoadingRow = (colSpan) => (
    <tr>
      <td colSpan={colSpan} className="text-center py-4">
        <Spinner size="sm" color="primary" className="mr-2" />
        Memuat data...
      </td>
    </tr>
  );

  const renderEmptyRow = (message, colSpan) => (
    <tr>
      <td colSpan={colSpan} className="text-center py-4 text-muted">
        {message}
      </td>
    </tr>
  );

  return (
    <div className="penyedia-page">
      <Row className="gy-4">
        <Col lg={12} className="mb-4">
          <Widget
            title="Data Penyedia"
            containerClass="shadow-sm border-0"
            className="p-0"
          >
            <Card className="border-0">
              <CardHeader className="bg-transparent border-0 d-flex justify-content-between align-items-center px-3 py-3">
                <span className="mb-0 font-weight-semibold">
                  Daftar Penyedia
                </span>
                <Button color="primary" size="sm" onClick={handleAddPenyedia}>
                  Tambah Penyedia
                </Button>
              </CardHeader>
              <CardBody className="px-3 pb-3">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: "5%" }}>No</th>
                        <th style={{ width: "40%" }}>Nama Penyedia</th>
                        <th style={{ width: "15%" }}>Tahun</th>
                        <th style={{ width: "20%" }} className="text-center">
                          Aksi
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {penyediaState.loading
                        ? renderLoadingRow(4)
                        : penyediaList.length === 0
                        ? renderEmptyRow("Belum ada data penyedia", 4)
                        : penyediaList.map((item, index) => (
                            <tr key={item.id}>
                              <td>{index + 1}</td>
                              <td>{item.nama}</td>
                              <td>{item.tahun}</td>
                              <td className="text-center">
                                <Button
                                  size="sm"
                                  color="info"
                                  className="mr-2"
                                  onClick={() => handleEditPenyedia(item)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  color="danger"
                                  onClick={() => handleDeletePenyedia(item)}
                                >
                                  Hapus
                                </Button>
                              </td>
                            </tr>
                          ))}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Widget>
        </Col>
      </Row>

      <Modal isOpen={penyediaModalOpen} toggle={togglePenyediaModal}>
        <Form onSubmit={handleSavePenyedia}>
          <ModalHeader toggle={togglePenyediaModal}>
            {editingPenyedia ? "Edit Penyedia" : "Tambah Penyedia"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="nama-penyedia">
                Nama Penyedia <span className="text-danger">*</span>
              </Label>
              <Input
                id="nama-penyedia"
                type="text"
                value={namaPenyedia}
                onChange={(event) => setNamaPenyedia(event.target.value)}
                placeholder="Masukkan nama penyedia"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="tahun-penyedia">
                Tahun <span className="text-danger">*</span>
              </Label>
              <Input
                id="tahun-penyedia"
                type="number"
                value={tahunPenyedia}
                onChange={(event) => setTahunPenyedia(event.target.value)}
                placeholder="Masukkan tahun (contoh: 2024)"
                min="2000"
                max="2100"
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={togglePenyediaModal}>
              Batal
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" color="light" className="mr-2" />
                  Menyimpan...
                </>
              ) : editingPenyedia ? (
                "Simpan Perubahan"
              ) : (
                "Tambah"
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default TablePenyedia;
